import type { Any, TypeOf } from 'io-ts';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import { leaveSessionIfAccountDeleted } from '@/vue/features/shared/auth/services/userSession';
import axios from '../../axios';
import { ErrorHandler, Significance } from './Builder/RequestBuilder';
import { useUserStore } from '@/vue/stores/user';
import { useIsAnonymousStore } from '@/vue/stores/isAnonymous';
import { useLoadingStore } from '../../../vue/stores/loading';
import type {
    AxiosErrorWithResponse } from '../../mixins/defaultAjaxErrorHandler';
import {
    defaultAjaxErrorHandlerFunction,
} from '../../mixins/defaultAjaxErrorHandler';
import { decodeAsTypeC } from '../../typeAssertions/isTypeC';
import ROUTE_NAMES from '../../../vue/routes/routeNames';
import router from '../../../vue/router';
import getFirstPermittedAdminRoute
    from '../../helpers/functions/permissions/getFirstPermittedAdminRoute';

const STATUS_FORBIDDEN = 403;
const forbiddenAdminRoutes = new Set<string>();

export default class Request<T extends Any> {
    private responseStatusCode?: number;

    private responseHeaders?: Record<string, unknown>;

    constructor(
        private readonly config: AxiosRequestConfig,
        private readonly significance: Significance,
        private readonly errorHandler: ErrorHandler,
        private readonly responseType: T,
        private readonly is403Allowed: boolean,
        private readonly handledErrorStatuses: number[],
    ) {
    }

    private async redirectIfNeeded({ response }: AxiosError) {
        const currentRoute = router.currentRoute.value;
        const isCurrentRouteLogin =
            currentRoute.name === ROUTE_NAMES.LOGIN_MAIN;

        const isCurrentRouteAllowedForAnonymous = useIsAnonymousStore().isAnonymous;

        if (isCurrentRouteLogin || isCurrentRouteAllowedForAnonymous) {
            return;
        }

        const status = response?.status;
        const isUnauthorized = status === 401;
        const isForbidden = status === STATUS_FORBIDDEN;

        if (isUnauthorized) {
            localStorage.clear();
            useUserStore().reset();
            await router.push({ name: ROUTE_NAMES.LOGIN_MAIN });
            return;
        }

        if (isForbidden && this.is403Allowed) {
            return;
        }

        if (!isForbidden) {
            return;
        }

        if (await leaveSessionIfAccountDeleted(response?.data)) {
            return;
        }

        const currentRouteName = String(currentRoute.name);
        const isAdminRoute = currentRoute.matched.some(
            (record) => record.name === ROUTE_NAMES.ADMIN_MAIN,
        );

        if (isAdminRoute) {
            forbiddenAdminRoutes.add(currentRouteName);

            const nextRoute = getFirstPermittedAdminRoute(
                currentRouteName,
                forbiddenAdminRoutes,
            );

            if (!nextRoute || nextRoute === currentRouteName) {
                return;
            }

            await router.push({ name: nextRoute });
        } else {
            await router.push({ name: ROUTE_NAMES.HOME });
        }
    }

    public async send(): Promise<TypeOf<T>> {
        this.incrementRequestsCount();

        try {
            const { data, status, headers } = await axios.request(this.config);
            this.responseStatusCode = status;
            this.responseHeaders = { ...headers };
            return decodeAsTypeC(
                data,
                this.responseType,
                `Response from ${this.config.url}`,
            );
        } catch (e: unknown) {
            if (axios.isAxiosError(e)) {
                this.responseStatusCode = e.response?.status;
                await this.redirectIfNeeded(e);
                this.handleError(e);
            }

            throw e;
        } finally {
            this.decrementRequestsCount();
        }
    }

    public getResponseStatusCode(): number {
        if (this.responseStatusCode === undefined) {
            throw new Error('You must await the send function before getting the status code');
        }

        return this.responseStatusCode;
    }

    public getResponseHeader(name: string): string | undefined {
        if (this.responseHeaders === undefined) {
            throw new Error('You must await the send function before getting a response header');
        }

        const value = this.responseHeaders[name.toLowerCase()];

        return typeof value === 'string' ? value : undefined;
    }

    private incrementRequestsCount(): void {
        const { incrementAjaxRequests, incrementItemsToLoad } = useLoadingStore();

        if (this.significance === Significance.Main) {
            incrementItemsToLoad();
        }
        if (this.significance === Significance.Secondary) {
            incrementAjaxRequests();
        }
    }

    private decrementRequestsCount(): void {
        const { decrementAjaxRequests, decrementItemsToLoad } = useLoadingStore();

        if (this.significance === Significance.Main) {
            decrementItemsToLoad();
        }
        if (this.significance === Significance.Secondary) {
            decrementAjaxRequests();
        }
    }

    private handleError(e: unknown): void {
        if (axios.isCancel(e)) {
            return;
        }
        if (axios.isAxiosError(e) && e.response?.status === STATUS_FORBIDDEN) {
            return;
        }
        if (axios.isAxiosError(e) && this.handledErrorStatuses.includes(e.response?.status ?? 0)) {
            return;
        }

        if (this.errorHandler === ErrorHandler.Default) {
            defaultAjaxErrorHandlerFunction(e as AxiosErrorWithResponse);
        }
    }
}
